import { PayoutDealValues } from '../pages/SingleCheckoutPage/DealValues/PayoutDealValues'
import { captureException } from '@sentry/nextjs'
import { Form, Formik } from 'formik'
import { uniq } from 'lodash'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useContext, useEffect, useState } from 'react'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import * as Yup from 'yup'
import { customerState } from '@/app/auth/auth.state'
import { ClientOnly } from '@/app/common/components/ClientOnly'
import { ModalDialogContext } from '@/app/common/context/modalDialogContext'
import { useStoryblokConfig } from '@/app/common/context/storyblokConfigContext'
import { useQuery } from '@/app/common/graphql/hooks'
import { Button } from '@/components/Button'
import Input from '@/components/Input'
import { UnderlinedLink } from '@/components/UnderlinedLink'
import {
  useCheckCheckoutDate,
  useCreateDeal,
  useIsSimplifiedPawnCheckout,
} from '@/domains/checkout/checkout.service'
import {
  customDealIdState,
  dealBookingNumberState,
  dealDurationInDaysState,
  feedbackSentenceState,
  knpDebtState,
  selectedItemsState,
} from '@/domains/checkout/checkout.state'
import { CheckoutHeaderV2 } from '@/domains/checkout/components/CheckoutHeader/CheckoutHeaderV2'
import MapsDialog from '@/domains/checkout/components/MapsDialog'
import { QuestionMark } from '@/domains/checkout/components/QuestionMark'
import { AvailablePayoutOptions } from '@/domains/checkout/pages/SingleCheckoutPage/DealValues/AvailablePayoutOptions'
import { DatabaseItem } from '@/helpers/estimateDealMargin'
import { getStoryBlokLink } from '@/helpers/getStoryBlokLink'
import product from '@/helpers/product'
import { resizedProductImageLoader } from '@/helpers/resizedProductImageLoader'
import * as tracking from '@/helpers/tracking'
import useCompany from '@/helpers/useCompany'
import { useNavigateToPathBookmark } from '@/helpers/usePathBookmarks'
import useRegionCode from '@/helpers/useRegionCode'
import { ErrorBox } from '@/style/components/ErrorBox/ErrorBox'
import { LastFallbackImage, UploadedImage } from '@/style/components/Image'
import theme from '@/style/themes/default'
import { MainCategoryType } from '@/types'
import {
  DealItemCreateArgs,
  DealPawnDurationsDocument,
  DealPawnDurationsQuery,
  DealPawnDurationsQueryVariables,
  EDealPartnerType,
  EDealType,
  EManualPaymentType,
  ERegionCode,
  GetShopsForCategoriesDocument,
  GetShopsForCategoriesQuery,
  GetShopsForCategoriesQueryVariables,
  MutationCreateDealArgs,
  PersistedDealCalculation,
} from '@/types/gql/graphql'
import { cn } from '@/utils/cn'
import { printLocalAmount } from '@/utils/misc'
import PinIcon from './assets/pin.svg'

const dealTypes = [
  {
    type: EDealType.Purchase,
    titleTrans: 'common:selling',
  },
  {
    type: EDealType.Pawn,
    titleTrans: 'common:pawn',
  },
]

interface CheckoutFormValues {
  email: string
  agbAccepted: boolean
}

interface Props {
  dealCalculation: PersistedDealCalculation | undefined
  dealType: EDealType
  handleDealTypeChange: (value: EDealType) => void
  disableChangeDealType?: boolean
  hideEmailInput?: boolean
}

export function SingleCheckoutSection({
  dealCalculation,
  dealType,
  handleDealTypeChange,
  disableChangeDealType,
  hideEmailInput,
}: Props) {
  const navigateToPathBookmark = useNavigateToPathBookmark('/')
  const { t } = useTranslation()
  const modal = useContext(ModalDialogContext)
  const router = useRouter()
  const company = useCompany()
  const { config } = useStoryblokConfig()
  const regionCode = useRegionCode()

  const customer = useRecoilValue(customerState)
  const isSimplifiedPawnCheckout = useIsSimplifiedPawnCheckout()
  const knpDebt = useRecoilValue(knpDebtState)
  const setFeedbackSentence = useSetRecoilState(feedbackSentenceState)
  const setDealBookingNumber = useSetRecoilState(dealBookingNumberState)
  const [customDealId, setCustomDealId] = useRecoilState(customDealIdState)
  const [selectedItems, setSelectedItems] = useRecoilState(selectedItemsState)

  const [durationInDays, setDurationInDays] = useRecoilState(
    dealDurationInDaysState,
  )
  const [loanDurationError, setLoanDurationError] = useState('')

  const companyName =
    regionCode === ERegionCode.De ? 'Cashy Germany GmbH' : 'Cashy Austria GmbH'

  const itemCategoryIds = uniq(
    selectedItems?.map((item) => item.itemCategoryId),
  )

  useCheckCheckoutDate()

  const shops = useQuery<
    GetShopsForCategoriesQuery,
    GetShopsForCategoriesQueryVariables
  >(GetShopsForCategoriesDocument, {
    variables: {
      itemCategoryIds: itemCategoryIds ?? [],
      opts: {
        filter: {
          regionCode,
        },
      },
    },
  })

  const { data: pawnDurationsData } = useQuery<
    DealPawnDurationsQuery,
    DealPawnDurationsQueryVariables
  >(DealPawnDurationsDocument, {
    variables: {
      args: {
        itemCategoryIds,
        companyId: company?._id,
      },
    },
    skip: !company || dealType !== EDealType.Pawn,
    onCompleted: (data) => {
      if (
        durationInDays &&
        data?.dealPawnDurations &&
        (durationInDays === undefined ||
          !data.dealPawnDurations.includes(durationInDays)) &&
        data.dealPawnDurations[0]
      ) {
        setDurationInDays(data.dealPawnDurations[0])
        setLoanDurationError(data.dealPawnDurations.join(', '))
      } else {
        setLoanDurationError('')
      }
    },
  })

  useEffect(() => {
    const handleNavigation = () => {
      if (customDealId) {
        setCustomDealId(undefined)
        setSelectedItems((items) => items.filter((item) => !item.customDealId))
      }
      if (
        company?.configuration.minimumPawnDuration &&
        durationInDays &&
        durationInDays >= company.configuration.minimumPawnDuration
      ) {
        setDurationInDays(company.configuration.minimumPawnDuration)
      }
    }
    router.events.on('routeChangeComplete', handleNavigation)
    return () => {
      router.events.off('routeChangeComplete', handleNavigation)
    }
  }, [
    customDealId,
    router.events,
    setCustomDealId,
    setSelectedItems,
    durationInDays,
    company?.configuration.minimumPawnDuration,
    setDurationInDays,
  ])

  const validationSchema = Yup.object().shape({
    email: hideEmailInput
      ? Yup.string().notRequired()
      : Yup.string()
          .email(t('common:errors.must_be_valid_email'))
          .required(t('common:errors.is_required')),
    agbAccepted: Yup.boolean()
      .oneOf([true], t('common:errors.terms_must_be_accepted'))
      .required(t('common:errors.terms_must_be_accepted')),
  })

  const initialValues: CheckoutFormValues = {
    email: customer?.email ?? '',
    agbAccepted: false,
  }

  const { createDeal } = useCreateDeal()

  const handleSubmit = async (values: CheckoutFormValues) => {
    if (!(dealCalculation && selectedItems && company)) {
      throw new Error('Missing required data')
    }

    const submissionArgs: MutationCreateDealArgs = {
      dealCalculationId: dealCalculation.dealCalculationId,
      companyId: company._id,
      registeredCustomerId: customer?._id,
      guestCustomerDataArgs: values.email
        ? {
            email: values.email,
          }
        : undefined,
      payoutType: EManualPaymentType.Cash,
      dealItemsCreateArgs: selectedItems.map(
        (item): DealItemCreateArgs => ({
          answers: item.answers,
          customPayoutAmount: item.customPayoutAmount,
          itemCategoryId: item.itemCategoryId,
          algoliaReference: item.algoliaReference,
          media: item.media,
          title: item.selectedVariant?.name ?? item.title,
          material: item.material,
          ean: item.ean,
          note: item.note,
          policeNumber: item.policeNumber,
          isInvestmentGold: item.isInvestmentGold,
          variantId: item.variantId,
          unconfirmedPropertyIds: item.unconfirmedPropertyIds,
        }),
      ),
    }

    if (customDealId) {
      submissionArgs.customDealId = customDealId
    }

    if (knpDebt?.loadedKnpDebt) {
      submissionArgs.dealPartnerData = {
        type: EDealPartnerType.Knp,
        knp: {
          debtAmount: knpDebt?.loadedKnpDebt.debtAmount,
          fileReferenceNumber: knpDebt.loadedKnpDebt.fileReferenceNumber,
        },
      }
    }

    try {
      const { data } = await createDeal({
        variables: { ...submissionArgs },
      })

      if (!data) return

      try {
        if (!submissionArgs.customDealId) {
          tracking.trackDealBooked({
            type:
              data.createDeal.dealType === EDealType.Pawn ? 'pawn' : 'purchase',
            dealId: data.createDeal._id,
            items: data.createDeal.items.map(
              (item): DatabaseItem => ({
                categoryHierarchy: item.itemCategory.parentCategories.map(
                  (c) => c._id,
                ),
                payout: item.values.verified?.payoutAmount,
              }),
            ),
          })
        }

        tracking.purchase(
          selectedItems.map((item, i) => ({
            ...item,
            actualValue:
              dealCalculation.itemsValuesEntries[i].payoutAmount || undefined,
          })),
          {
            transaction_id: data.createDeal._id,
            type: dealType === EDealType.Pawn ? 'pawn' : 'purchase',
          },
        )
      } finally {
        setDealBookingNumber(data.createDeal.bookingNumber)

        setFeedbackSentence({
          paymentData: {
            paymentType: EManualPaymentType.Cash,
          },
          payoutAmount: dealCalculation.dealValuesEntry.payoutAmount,
          email: values.email,
          availableShops: shops.data?.getShopsForCategories ?? [],
        })

        router.replace('/checkout/feedback')
        await new Promise((resolve) => setTimeout(resolve, 1_000))
      }
    } catch (error) {
      console.error(error)
      captureException(error)
    }
  }

  return (
    <>
      <CheckoutHeaderV2 onClose={navigateToPathBookmark} />
      <ClientOnly>
        <div className="flex justify-center w-screen pt-12">
          <div className="flex flex-col gap-12 px-4 py-6 w-screen max-w-[600px]">
            <div className="text-center flex flex-col gap-2">
              <h1 className="text-heading-m">
                {t(
                  dealType === EDealType.Pawn
                    ? 'checkout:pawn_preview'
                    : 'checkout:purchase_preview',
                )}
              </h1>
              <span className="text-m text-secondary">
                {t('checkout:verify_data_and_complete')}
              </span>
            </div>
            <div>
              <h2 className="text-heading-2xs text-tertiary uppercase mb-1">
                {t(
                  dealType === EDealType.Pawn
                    ? 'checkout:your_pawn_security'
                    : 'checkout:your_purchase',
                )}
              </h2>
              <div
                className={cn(
                  'bg-white rounded-xl border border-secondary p-4 flex flex-col gap-3',
                  dealType === EDealType.Purchase && 'drop-shadow-md',
                )}
              >
                {selectedItems.map((item, i) => {
                  return (
                    <div className="flex flex-row gap-4">
                      {item.selectedVariant?.image ? (
                        <div className="relative size-16 md:size-20">
                          <Image
                            loader={resizedProductImageLoader}
                            fill
                            sizes={`(max-width: ${theme.breakpoints.md}px) 64px, 90px`}
                            src={item.selectedVariant?.image.url}
                            alt={item.selectedVariant?.image.id}
                          />
                        </div>
                      ) : item.picture ? (
                        <div className="w-16 max-h-16 h-full md:max-w-24 md:max-h-20">
                          <UploadedImage
                            data={item.picture}
                            subset={['product_small', 'product_thumbnail']}
                            sizes={{
                              md: 'product_small',
                            }}
                            primarySize="product_small"
                          />
                        </div>
                      ) : (
                        <div className="w-16 max-h-16 h-full md:max-w-24 md:max-h-20">
                          <LastFallbackImage />
                        </div>
                      )}
                      <div className="flex flex-col gap-1">
                        <span className="text-heading-2xs">{item.title}</span>
                        <div className="flex flex-col gap-0.5">
                          {!item.itemCategory?.parentCategories?.some(
                            (category) =>
                              category.name === MainCategoryType.JEWELLERY,
                          ) &&
                            product
                              .formatVariantProperties(
                                item?.selectedVariant,
                                t,
                                router.locale,
                              )
                              ?.map((property) => {
                                return (
                                  <span className="text-xs text-tertiary">
                                    {property.title}: {property.value}
                                  </span>
                                )
                              })}
                        </div>
                      </div>
                      {dealCalculation && dealType === EDealType.Purchase && (
                        <div className="ml-auto flex flex-col justify-center">
                          <span className="text-xl font-semibold text-brand-secondary">
                            {printLocalAmount({
                              number:
                                dealCalculation?.itemsValuesEntries[i]
                                  ?.payoutAmount ?? 0,
                              removeSpaces: true,
                              fractionDigits: 0,
                            })}
                          </span>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
              {dealType === EDealType.Purchase && (
                <AvailablePayoutOptions className="-mt-4 pt-6 pb-2 px-4 " />
              )}
              <span className="text-s text-tertiary">
                {t('checkout:single_checkout.add_more_items_question')}
                <QuestionMark
                  onClick={() => {
                    modal.open(
                      <div className="bg-white rounded-xl p-4 pr-8 max-w-[600px] relative">
                        {t(
                          'checkout:single_checkout.add_more_items_explanation',
                        )}
                        <button
                          className="absolute top-0 right-0 text-2xl size-10"
                          onClick={() => modal.close()}
                        >
                          ×
                        </button>
                      </div>,
                      {
                        customStyles: {
                          content: {
                            display: 'inline-block',
                            maxWidth: '80%',
                          },
                        },
                      },
                    )
                  }}
                />
              </span>
            </div>
            {!isSimplifiedPawnCheckout && !disableChangeDealType && (
              <div>
                <h2 className="text-heading-2xs text-tertiary uppercase mb-1">
                  {t('checkout:single_checkout.choose_deal_type')}
                </h2>
                <div className="flex flex-row gap-2">
                  {dealTypes.map(({ type, titleTrans }) => (
                    <Button
                      variant="secondary"
                      key={type}
                      onClick={() => handleDealTypeChange(type)}
                      className={cn(
                        'flex-1 rounded-xl border fill-fill-brand-tertiary text-l font-semibold py-4 input-border',
                        type === dealType
                          ? 'border-brand bg-fill-brand-tertiary'
                          : 'border-secondary bg-white',
                      )}
                    >
                      {t(titleTrans)}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            {dealType === EDealType.Pawn &&
              pawnDurationsData?.dealPawnDurations &&
              pawnDurationsData.dealPawnDurations.length > 1 && (
                <div>
                  <h2 className="text-heading-2xs text-tertiary uppercase mb-1">
                    {t('common:pick_loan_duration')}
                  </h2>
                  <div className="flex flex-row gap-2">
                    {pawnDurationsData.dealPawnDurations.map((duration) => (
                      <Button
                        variant="secondary"
                        key={duration}
                        className={cn(
                          'flex-1 rounded-xl border fill-fill-brand-tertiary text-l font-semibold py-4 input-border',
                          duration ===
                            dealCalculation?.dealValuesEntry?.durationInDays
                            ? 'border-brand bg-fill-brand-tertiary'
                            : 'border-secondary bg-white',
                        )}
                        onClick={() => setDurationInDays(duration)}
                      >
                        {duration} {t('common:days')}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            {dealType === EDealType.Pawn && dealCalculation && (
              <PayoutDealValues
                dealValuesEntry={dealCalculation.dealValuesEntry}
                appliedUsedFeeDefinitions={
                  dealCalculation.appliedUsedFeeDefinitions
                }
                dealItems={selectedItems}
                dealType={dealType}
                knpDebt={knpDebt}
                showKnp={true}
              />
            )}
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, isSubmitting, handleChange }) => (
                <Form className="flex flex-col gap-12">
                  {!hideEmailInput && (
                    <div>
                      <h2 className="text-heading-2xs text-tertiary uppercase mb-1">
                        {t('checkout:single_checkout.email_info_sent')}
                      </h2>
                      <Input
                        name="email"
                        label={t('checkout:single_checkout.email_label')}
                        onChange={handleChange}
                        value={values.email}
                        error={
                          touched.email && errors.email
                            ? errors.email
                            : undefined
                        }
                      />
                    </div>
                  )}
                  <div className="flex flex-col gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        modal.open(
                          <MapsDialog
                            shops={shops.data?.getShopsForCategories ?? []}
                          />,
                          {
                            variant: 'full',
                          },
                        )
                      }}
                      className="text-start border rounded-xl border-accent bg-fill-accent-tertiary flex flex-row gap-3 py-2 px-3"
                    >
                      <div className="size-4">
                        <PinIcon />
                      </div>

                      <span className="text-s text-secondary">
                        {t('checkout:pickup_money_after_reservation')}{' '}
                        <span className="font-semibold text-brand-secondary">
                          {t('checkout:more_info_about_locations')}
                        </span>
                      </span>
                    </button>
                    <div className="flex flex-row gap-2 items-start">
                      <input
                        id="CHECKOUT_OVERVIEW_PRIVACY_POLICY_CHECKBOX"
                        name="agbAccepted"
                        className="accent-primary size-[18px] border-2 rounded-[2px] mt-0.5"
                        type="checkbox"
                        onChange={handleChange}
                        checked={values.agbAccepted}
                      />
                      <label
                        htmlFor="CHECKOUT_OVERVIEW_PRIVACY_POLICY_CHECKBOX"
                        className="text-m text-tertiary"
                      >
                        <Trans
                          i18nKey="common:terms_and_conditions.agb_and_privacy_policy"
                          values={{
                            companyName,
                          }}
                          components={{
                            AGBLink: (
                              <UnderlinedLink
                                className="p-0"
                                as="next-link"
                                href={getStoryBlokLink(config.agb)}
                                target="_blank"
                              />
                            ),
                            PrivacyPolicyLink: (
                              <UnderlinedLink
                                as="next-link"
                                href={getStoryBlokLink(config.privacy_policy)}
                                target="_blank"
                              />
                            ),
                          }}
                        />
                      </label>
                    </div>

                    {loanDurationError && (
                      <ErrorBox
                        message={t('common:pick_loan_duration_error').replace(
                          '{{value}}',
                          loanDurationError,
                        )}
                      />
                    )}
                    {selectedItems.length > 0 &&
                      (dealCalculation?.dealValuesEntry.payoutAmount ?? 0) <
                        30 && (
                        <ErrorBox
                          message={t('checkout:payout_amount_min_30')}
                        />
                      )}

                    <Button
                      type="submit"
                      className="rounded-xl text-l font-semibold p-3"
                      loading={isSubmitting}
                      disabled={isSubmitting || !values.agbAccepted}
                    >
                      {dealType === EDealType.Pawn
                        ? t('checkout:book_pawn')
                        : t('checkout:book_purchase')}
                    </Button>
                    {!disableChangeDealType && (
                      <Button
                        type="button"
                        onClick={() => {
                          handleDealTypeChange(
                            dealType === EDealType.Pawn
                              ? EDealType.Purchase
                              : EDealType.Pawn,
                          )
                        }}
                        className="text-brand-secondary font-semibold text-l"
                        variant="ghost"
                      >
                        {dealType === EDealType.Pawn
                          ? t('checkout:switch_to_purchase')
                          : t('checkout:switch_to_pawn')}
                      </Button>
                    )}
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </ClientOnly>
    </>
  )
}
